import { Component, h, Prop, Element, getAssetPath } from '@stencil/core';
import { ComponentColors, initColors } from '../../utils/constants/colors';
import { z } from 'zod';
import { translationService, TranslationsProps } from '../../utils/services/translationService';

export const LogoPositions = z.enum(['default', 'column', 'row']);
export type LogoPositions = z.infer<typeof LogoPositions>;
export const LogoSpacing = z.enum(['default', 'spacious']);
export type LogoSpacing = z.infer<typeof LogoSpacing>;
export const Fields = z.enum(['name', 'address', 'zipCode', 'city', 'distance']);
export type Fields = z.infer<typeof Fields>;
export const ASL_StoreInfoCardProps = z.object({
  logoPositions: LogoPositions.optional(),
  logoSpacing: LogoSpacing.optional(),
  componentColors: ComponentColors.optional(),
  translationsProps: TranslationsProps.optional(),
});
export type ASL_StoreInfoCardProps = z.infer<typeof ASL_StoreInfoCardProps>;

@Component({
  tag: 'aico-store-locator-store-info-card',
  styleUrl: 'aico-store-locator-store-info-card.css',
  shadow: true,
  assetsDirs: ['../assets']
})
export class AicoStoreLocatorStoreInfoCard {
  @Prop() refKey: number = -1;

  /*
    Store Information
  */
  @Prop() organisationName: string = "";
  @Prop() addressLine: string = "";
  @Prop() zipCode: string = "";
  @Prop() city: string = "";
  @Prop() distance: number = 0;
  @Prop() excludedFields: Fields[] = [];
  /*
    Brands Information
  */
  @Prop() brandsLogos: string[] = [];
  @Prop() logoPositions: LogoPositions = 'default';
  @Prop() logoSpacing: LogoSpacing = 'default';
  @Prop() isMobile: boolean = false;


  @Element() el!: HTMLElement;
  @Prop() componentColors?: ComponentColors;

  @Prop() props?: ASL_StoreInfoCardProps;

  async componentWillLoad() {
    if (this.props) {
      const parsedProps = ASL_StoreInfoCardProps.parse(this.props);
      this.logoPositions = parsedProps.logoPositions ?? this.logoPositions;
      this.logoSpacing = parsedProps.logoSpacing ?? this.logoSpacing;
      this.componentColors = parsedProps.componentColors ?? this.componentColors;
    }

    await translationService.initComponentLoad('aico-store-locator-store-info-card');

    if (this.componentColors) {
      this.componentColors = ComponentColors.parse(this.componentColors);
      initColors(this.el, this.componentColors);
    }
  }

  private t(key:string, locale?:string): string {
    return translationService.getTranslation('aico-store-locator-store-info-card', key, locale);
  }

  private canRenderField(field: Fields): boolean {
    return !this.excludedFields.includes(field);
  }

  render() {
    const mainLogo = this.brandsLogos[0] || null;
    const otherLogos = this.brandsLogos.slice(1);


    const hasExtraLines =
      (this.canRenderField('address') && this.addressLine != "") ||
      (this.canRenderField('zipCode') && (this.zipCode != "" || this.city != "")) ||
      (this.canRenderField('distance') && this.distance > 0);

    const rowMode = this.logoPositions === 'row' && this.brandsLogos.length > 0;

    const logoCls = (i: number, len: number) => {
      if (len === 1) return 'w-6 h-6 rounded-md';

      if (this.logoSpacing === 'spacious') {
        return `w-6 h-6 rounded-md ${i ? 'ml-1' : ''}`;
      }
      const round =
        i === 0 ? 'rounded-l-md' :
          i === len - 1 ? 'rounded-r-md' :
            '';
      return `w-6 h-6 ${round}`;
    };

    return (
      <div key={this.refKey} class="flex p-2 w-full">
        {this.logoPositions !== 'row' && mainLogo != undefined && (
          <div class="flex flex-col flex-shrink-0 items-center mr-2">
            <img src={getAssetPath(mainLogo)} alt="Main Brand"
              class="rounded-md w-7 h-7" />
            {this.logoPositions === 'column' && otherLogos.length > 0 && (
              <div class="flex flex-col items-center mt-2">
                {otherLogos.map((logo, i) => {
                  if (logo != undefined)
                    return (<img key={i}
                      src={getAssetPath(logo)}
                      alt={`Brand ${i + 1}`}
                      class={logoCls(i, this.brandsLogos.length)} />)
                })}
              </div>
            )}
          </div>
        )}

        <div class="flex-grow min-w-0">
          <div class="flex items-start mb-1 w-full">
            <h3 class="flex-1 font-semibold text-[--primary-text-color] text-xl truncate">
              {this.organisationName || 'Unknown Store'}
            </h3>
          </div>

          {hasExtraLines ? (
            <div class={`flex justify-between items-start w-full ${this.isMobile ? '' : 'max-w-96'}`}>
              <div class="flex-grow min-w-0">
                {this.canRenderField('address') && this.addressLine && (
                  <p class="text-[--secondary-text-color] text-sm truncate">
                    {this.addressLine}
                  </p>
                )}

                {(this.canRenderField('zipCode') || this.canRenderField('city')) && (
                  <p class="text-[--secondary-text-color] text-sm truncate">
                    {this.canRenderField('zipCode') && `${this.zipCode} `}
                    {this.canRenderField('city') && this.city}
                  </p>
                )}

                {this.canRenderField('distance') && this.distance > 0 && (
                  <div class="flex items-center gap-1 text-[--tertiary-text-color]">
                    <svg xmlns="http://www.w3.org/2000/svg"
                      class="w-3 h-3" fill="none"
                      viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span class="text-xs truncate">
                      {this.distance < 1
                        ? `${(this.distance * 1000).toFixed(0)} meters ${this.t('away')}`
                        : `${this.distance.toFixed(0)} km ${this.t('away')}`}
                    </span>
                  </div>
                )}
              </div>

              {rowMode && (
                <div class="flex flex-shrink-0 self-end">
                  {this.brandsLogos.map((logo, i) => {
                    if (logo != undefined)
                      return (<img key={i}
                        src={getAssetPath(logo)}
                        alt={`Brand ${i + 1}`}
                        class={logoCls(i, this.brandsLogos.length)} />)
                  })}
                </div>
              )}
            </div>
          ) : null}
        </div>
      </div>
    );
  }
}
